import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { Mail } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { GenericTable, Spinner } from '@/modules/common';
import { EventOrder } from '@/modules/eventsV2/types';

import { getOrdersHistoryColumns } from './columns';
import { OrdersFilters, SendSmsDialog, OrdersHistoryEventActions } from './components';
import { useEventOrdersHistory } from "@/modules/events/api";
import { useEventFilters } from "@/modules/eventsV2/hooks";

export const OrdersHistoryTab = () => {
  const params = useParams<{ id: string }>();
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [sendOneSmsOrder, setSendOneSmsOrder] = useState(null);
  const [isSendSmsDialogOpen, setIsSendSmsDialogOpen] = useState(false);

  const { getValues } = useFormContext();
  const { filters, filtersChange } = useEventFilters();

  const eventId = params.id;
  const eventName = getValues('name');

  const {
    isFetchingOrders,
    ordersData,
    isGeneratingReport,
    generateReport,
  } = useEventOrdersHistory(eventId, eventName, selectedOrders, filters);

  const onSendSms = (id: string) => {
    setSendOneSmsOrder(id);
    setIsSendSmsDialogOpen(true);
  };

  const columns = getOrdersHistoryColumns(eventId, onSendSms);

  if (isFetchingOrders) {
    return <Spinner />;
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="font-semibold text-lg">Orders Table</p>

      <div className="flex flex-col gap-4">
        <GenericTable<EventOrder>
          pinEnabled
          columns={columns}
          name="event-orders"
          data={ordersData}
          emptyCellClassName="h-20"
          sizeStyleProperty="minWidth"
          tableCellClassName="px-2 py-1"
          tableRowClassName="hover:bg-transparent"
          isFiltered={Object.keys(filters).length > 0}
          emptyTitle="No orders yet"
          emptyDescription=""
          onRowSelectionChange={setSelectedOrders}
          initialState={{
            columnPinning: {
              right: ['actions'],
              left: ['select'],
            },
          }}
        >
          <OrdersFilters filters={filters} onFilters={filtersChange} />

          {ordersData.length > 0 && (
            <OrdersHistoryEventActions
              onGenerateReport={generateReport}
              isGeneratingReport={isGeneratingReport}
            />
          )}

          {Object.keys(selectedOrders).length > 0 && (
            <Button
              size="sm"
              onClick={() => setIsSendSmsDialogOpen(true)}
              startIcon={<Mail />}
            >
              Send SMS
            </Button>
          )}
        </GenericTable>
      </div>

      <SendSmsDialog
        eventId={eventId}
        isOpen={isSendSmsDialogOpen}
        onOpenChange={() => {
          setIsSendSmsDialogOpen(false);
          if (sendOneSmsOrder) {
            setSendOneSmsOrder(null);
          }
        }}
        orders={ordersData}
        orderIds={sendOneSmsOrder ? [sendOneSmsOrder] : selectedOrders}
      />
    </div>
  );
};
